/**
 * `SourceAssessmentPane` — fixed bottom pane inside the
 * `WorkspaceFilePane` viewer shell. When the lawyer is viewing a
 * frozen `sources/<…>` original (PDF / DOCX / MD / audio / image),
 * THIS pane sits BELOW the viewer content and renders the source's
 * `.info` assessment (the lawyer's role/weight/context).
 *
 * Visual rules per the operator brief (2026-06-27):
 *  - **Fixed** at the bottom of the viewer (not collapsible — always-on
 *    when an eligible source is open).
 *  - **Contrasting background** from the white viewer above (slate-50
 *    on light, coffee-900 on dark) so the lawyer reads it as separate
 *    chrome attached to the file.
 *  - The `.info` content rendered as **HTML-formatted text**, not the
 *    raw `.info.md` markdown — structured labelled rows (status pill,
 *    role, weight, relevance, actors, notes, assessed by + when).
 *
 * Returns `null` when:
 *  - The lawyer is in the per-user org ("Global") workspace (CCM): source
 *    assessment is case-only, so the pane is suppressed in org scope.
 *  - The file isn't in `sources/` (raw-intake/, agent artifacts, etc.
 *    don't carry `.info` — the pane belongs to sources only).
 *  - The file's `.info.md` sibling isn't on disk yet (legacy sources
 *    that predate Part-G F1; the older sources in cadeiras don't have
 *    one). The pane is silent rather than empty to keep the viewer
 *    chrome clean for files where the concept doesn't apply.
 *
 * ## What this pane SHOWS (operator ruling 2026-08-09)
 *
 * **The ingest evaluation is the assessment.** `m-ingest-mission` runs an LLM
 * evaluation at ingest, so a source arrives here already carrying a brief on
 * what the document is (`summary` + `evaluation_rationale` + the model that
 * wrote them). That brief is the pane's primary content, rendered as
 * ANÁLISE AUTOMÁTICA and always attributed to the machine.
 *
 * ⚠ It renders INDEPENDENTLY of `status`. `status: "pending"` describes the
 * LAWYER's annotation, which is a different artifact by a different author on a
 * different clock — a complete machine brief beside a pending lawyer assessment
 * is the normal state of a freshly ingested document.
 *
 * ## ⚠ If you ever restore a write control here, pass `companionRef`
 *
 * This pane is READ-ONLY today, and its read is twin-safe by construction:
 * `useSourceInfoFile` computes the sibling `<stem>.info.md` from the OPENED
 * FILE'S PATH, so it always shows the record of the exact file on screen.
 *
 * The gateway pair (`case.source_assessment` / `case.source_assess_submit`)
 * resolves by `sourceId` instead — and one `sourceId` (sha256 of the original
 * bytes) can name TWO files when the same document was ingested twice. So the
 * moment anything here calls either method it must additionally pass
 * `companionRef: row.companion_ref`, verbatim from the `case.source_catalog`
 * row, or a lawyer's verdict can land on the wrong duplicate of their own
 * document. Runtime support is additive and already shipped
 * (`mission-ingest` `a5a8a717c`); omitting the field is not a no-op, it
 * silently restores the arbitrary-twin pick. See the wrappers in
 * `lib/gateway-rpc.ts` for the full contract.
 *
 * Recorded here rather than in a thread because the write surface was removed
 * (below) and whoever rebuilds it will start from this file, not from the bus
 * (handshake `20260811T220508Z` — countered because the SPA calls neither
 * method today, counter accepted).
 *
 * ⚠ **There is deliberately NO re-evaluate button.** It was built for a world
 * where ingestion was a mere upload and someone had to ask for an analysis;
 * with an LLM eval at ingest it offered to commission work already done, and it
 * was never bound to a mission or harness — it enqueued a concierge directive.
 * The lever for a deeper brief is the eval PROMPT in `m-ingest-mission`, which
 * improves every document at once. Do not reintroduce one without a ruling.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Maximize2,
  Minimize2,
  Calendar,
  Check,
  Edit3,
  FileText,
  Loader2,
  User,
  Workflow,
  Wrench,
} from "lucide-react";
import { AssessmentBadge } from "./AssessmentBadge";
import { DeepReportSection } from "./DeepReportSection";
import { useSourceInfoFile } from "@/hooks/useSourceInfoFile";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useActiveContextStore, useEffectiveScope } from "@/stores/useActiveContextStore";
import { basename, type FileRef } from "@/stores/useWorkspaceFileStore";
import type { SourceAssessment } from "@/types/gateway";
import { cn } from "@/lib/utils";

export interface SourceAssessmentPaneProps {
  file: FileRef;
}

export function SourceAssessmentPane({ file }: SourceAssessmentPaneProps) {
  const { t } = useTranslation();
  const { assessment, isPending, isError } = useSourceInfoFile(file);
  // Expanded mode (operator 2026-07-30): the pane takes the ENTIRE viewer
  // pane (absolute overlay inside the WorkspaceFilePane section, which is
  // `relative` for exactly this) and steps up to reading typography — the
  // chat's serif at 15px. Collapses back to the 40vh bottom strip.
  const [expanded, setExpanded] = useState(false);
  // A new file resets the mode — expansion is a per-document reading choice.
  useEffect(() => setExpanded(false), [file.path]);

  // ── Resizable strip (operator 2026-07-30) — the CaseNavigator handle
  // pattern verbatim: `null` = AUTO (content height, 40vh cap — the shipped
  // default, preserved until the lawyer actually drags); a drag on the TOP
  // edge takes an explicit px height. Session-local by design (a reading
  // posture, not a setting). Double-click / Enter returns to AUTO. Hidden in
  // EXPANDED mode — an overlay has no boundary to drag.
  const [paneHeight, setPaneHeight] = useState<number | null>(null);
  const asideRef = useRef<HTMLElement>(null);
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const PANE_MIN_PX = 56;
  const RESIZE_KEY_STEP_PX = 24;

  const measuredHeight = () => asideRef.current?.getBoundingClientRect().height ?? 0;
  const containerHeight = () =>
    asideRef.current?.parentElement?.getBoundingClientRect().height ?? 0;
  const commitHeight = (px: number) =>
    setPaneHeight(Math.min(Math.max(px, PANE_MIN_PX), Math.max(containerHeight() - 48, PANE_MIN_PX)));

  function onHandlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    const startH = measuredHeight();
    if (startH <= 0) return;
    dragRef.current = { startY: e.clientY, startH };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function onHandlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const d = dragRef.current;
    if (!d) return;
    // Top-edge handle: dragging UP grows the pane.
    commitHeight(d.startH - (e.clientY - d.startY));
  }
  function endDrag(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }
  function onHandleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const current = measuredHeight();
    if (e.key === "ArrowUp") {
      e.preventDefault();
      commitHeight(current + RESIZE_KEY_STEP_PX);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      commitHeight(current - RESIZE_KEY_STEP_PX);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setPaneHeight(null);
    }
  }
  // CCM org pane: source assessment is CASE-ONLY. The per-user org
  // ("Global") workspace carries no intake `.info` sidecars, so the pane is
  // suppressed there — otherwise the case-only first-assess CTA renders a
  // disabled, contextless state (its send needs a client/matter/case triple
  // that org scope lacks). The R3 `{scope:"org"}` doc.* methods are not
  // wired for `.info` reads either; case-only is the deliberate contract.
  const scope = useEffectiveScope();

  // No source eligibility → silent. The org workspace, and (in case scope)
  // raw-intake / artifacts / kb / .lexiun never carry `.info`; the pane
  // belongs to a case's `sources/` only.
  if (scope === "org" || !file.path.startsWith("sources/")) return null;

  // Derive the pane state:
  //  - loading: still fetching, no data yet
  //  - missing: read 404'd (legacy source pre-F1, or m-ingest-verify
  //              hasn't yet backfilled this one) — render the warning
  //              + CTA so the user can trigger a verify
  //  - assessed/pending/declined: render the body
  const state: "loading" | "missing" | "present" =
    isPending && !assessment
      ? "loading"
      : isError || !assessment
        ? "missing"
        : "present";

  return (
    <>
      {/* Resize handle — the pane's TOP edge (hidden in expanded/overlay
          mode: no boundary to drag there). Drag up = grow; double-click or
          Enter/Space = back to AUTO; ArrowUp/Down = keyboard steps. Same
          affordance grammar as the CASOS pane handle. */}
      {!expanded && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label={t("assessment.pane.resizeHandle")}
          tabIndex={0}
          data-testid="source-assessment-pane-resize-handle"
          data-dragging={dragging || undefined}
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDoubleClick={() => setPaneHeight(null)}
          onKeyDown={onHandleKeyDown}
          title={t("assessment.pane.resizeHandle")}
          className={cn(
            "relative shrink-0 basis-[5px] cursor-row-resize bg-transparent transition-colors",
            "hover:bg-gold-400/40 focus-visible:bg-gold-400/60 focus-visible:outline-none",
            dragging && "bg-gold-400/60",
          )}
        />
      )}
    <aside
      ref={asideRef}
      data-testid="source-assessment-pane"
      data-status={state === "present" ? assessment?.status : state}
      // `data-selectable-source` — text selected inside the .info pane
      // (e.g. lawyer highlights a Relevância sentence) gets piped into
      // `useSelectionContextStore` via App-shell `useSelectionCapture`.
      // Source attribution uses the SOURCE file's path (the .info is the
      // pane's content but the lawyer thinks of the selection as "from
      // this source") — keeps the chip's source label consistent with
      // the viewer above.
      data-selectable-source={file.path}
      aria-label={t("assessment.pane.ariaLabel")}
      // Scrollable: `overflow-y-auto` + a 40vh cap so a long assessment
      // (verbose notes / many actors) scrolls within the pane without
      // pushing the viewer above offscreen. Custom scrollbar styling
      // mirrors the rest of the LexIDE chrome (thin, subtle, theme-aware).
      data-expanded={expanded ? "true" : undefined}
      className={cn(
        "flex shrink-0 flex-col overflow-y-auto border-t border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 [scrollbar-color:theme(colors.slate.400)_transparent] [scrollbar-width:thin] dark:border-coffee-700/60 dark:bg-coffee-900 dark:text-cream-200 dark:[scrollbar-color:theme(colors.slate.600)_transparent]",
        expanded
          ? // Overlay the whole viewer pane; reading typography — the chat's
            // serif (font-reading) at 15px. Chrome inside (labels/footer)
            // pins itself back to sans explicitly.
            "absolute inset-0 z-20 max-h-none font-reading text-[15px] leading-[1.65] px-6 py-4"
          : cn(
              "min-h-[3.5rem] text-xs",
              // AUTO keeps the shipped 40vh cap; a dragged height replaces it.
              paneHeight == null && "max-h-[40vh]",
            ),
      )}
      style={!expanded && paneHeight != null ? { height: paneHeight } : undefined}
    >
      {state === "loading" && <PaneLoading />}
      {state === "missing" && <PaneMissing file={file} />}
      {state === "present" && assessment && (
        <PaneBody
          assessment={assessment}
          file={file}
          expanded={expanded}
          onToggleExpand={() => setExpanded((e) => !e)}
        />
      )}
    </aside>
    </>
  );
}

function PaneLoading() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
      <Loader2 size={12} className="animate-spin" />
      <span>{t("assessment.pane.loading")}</span>
    </div>
  );
}

/**
 * `useSourceAssessAction` — shared engine behind both the
 * first-assess CTA (`PaneMissing`) and the reassess affordance
 * (`PaneBody`). Sends an INVISIBLE directive to the concierge via
 * `runtime.system.enqueue_event` (the Claude-Code-style hidden-prompt
 * channel shipped at lexruntime `05db5028`).
 *
 * The directive text + the `source` tag differ by mode so the
 * runtime can distinguish "first-time assessment" from "re-evaluate
 * an existing .info" (the runtime side may preserve the prior
 * version or annotate the reply with a delta — see the
 * `source-reassess-lexruntime-handoff-2026-06-29` handoff).
 *
 * Heartbeat caveat (handback §4): the dev runtime has heartbeat
 * DISABLED, so the directive is observed on the concierge's NEXT
 * turn, not proactively. The CTA shows a confirmation chip after
 * click so the lawyer knows the directive went through even though
 * the visible response is deferred.
 */
export type AssessActionMode = "first" | "reassess";

/**
 * Shared engine for the assess directives. `mode:"first"` is the
 * single-click PaneMissing CTA; `mode:"reassess"` is invoked by
 * `SourceReassessDialog` after the lawyer optionally types guidance.
 *
 * `opts.guidance` (reassess-only) is the operator's free-form
 * instruction for THIS particular reassessment — e.g.
 *   "a new STJ ruling on this matter should be searched on the web;
 *    your prior Relevância conclusion is incorrect because…"
 * When present, the directive uses the `…WithGuidance` i18n variant
 * that wraps the user text in `<user_reassessment_guidance>` tags
 * (OWASP LLM01 spotlighting — same pattern as the selection-context
 * channel). When absent, falls back to the plain reassess directive.
 *
 * The `source` wire tag stays `lexide-spa:source-reassess-pane`
 * regardless of guidance presence — the runtime distinguishes
 * "first" vs "reassess" by tag, and "guided" vs "no guidance" by
 * the presence of the `<user_reassessment_guidance>` block in the
 * payload text. See
 * `ai-docs/handoffs/source-reassess-lexruntime-handoff-2026-06-29.md`
 * §3-bis (the post-handoff revision adding the guided variant).
 */
export function useSourceAssessAction(file: FileRef, mode: AssessActionMode) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const selection = useActiveContextStore((s) => s.selection);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const filename = file.title ?? basename(file.path);

  // WHY a reason and not a bare boolean: a disabled control must be able to
  // SAY why. "offline" (reconnect and retry) and "scope" (this case cannot
  // carry a directive at all) are different facts to a lawyer, and collapsing
  // them into one `false` is the state-collapsed-into-its-neighbour trap.
  //
  // ⚠ The clientId/matterId requirement is the RUNTIME's, and it is on its way
  // out: lexruntime `cdda34ae8` (NOT LIVE until the next fleet roll) drops it —
  // caseId alone builds the delivery key, so an UNASSIGNED flat case (D-8,
  // clientId null) will become sendable. Relaxing this gate to caseId-only is
  // deliberately NOT done here: it must wait for this lane's OWN probe against
  // the rolled runtime, the same discipline `CLIENT_DELETE_BACKEND_READY`
  // records — a peer's commit is not a running process.
  const blockedReason: "none" | "offline" | "scope" =
    !rpc || connectionState !== "connected"
      ? "offline"
      : !selection?.clientId || !selection?.matterId || !selection?.caseId
        ? "scope"
        : "none";
  const canSend = blockedReason === "none";

  // Resolves `true` only when the directive was actually enqueued — the
  // internal catch means the promise itself ALWAYS resolves, so callers
  // that latch UI state on completion must check this flag, not mere
  // settlement (the "failure that resolves" trap).
  async function handleAction(opts?: { guidance?: string }): Promise<boolean> {
    if (!canSend || !rpc) return false;
    setStatus("sending");
    try {
      const guidance = opts?.guidance?.trim();
      const directiveKey =
        mode === "first"
          ? "assessment.pane.missingDirective"
          : guidance
            ? "assessment.pane.reassessDirectiveWithGuidance"
            : "assessment.pane.reassessDirective";
      const sourceTag =
        mode === "first"
          ? "lexide-spa:source-assess-pane"
          : "lexide-spa:source-reassess-pane";
      await rpc.runtimeSystemEnqueueEvent({
        clientId: selection!.clientId!,
        matterId: selection!.matterId!,
        caseId: selection!.caseId!,
        text: t(directiveKey, { filename, guidance: guidance ?? "" }),
        source: sourceTag,
      });
      setStatus("sent");
      window.setTimeout(() => setStatus("idle"), 6000);
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[source-${mode}] runtime.system.enqueue_event failed`, err);
      setStatus("error");
      window.setTimeout(() => setStatus("idle"), 6000);
      return false;
    }
  }

  return { handleAction, status, canSend, blockedReason };
}

/**
 * `PaneMissing` — rendered when the source has NO `.info` sidecar on
 * disk (legacy source predating Part-G F1, or m-ingest-verify hasn't
 * yet reconciled this one). The pane stays MOUNTED for every
 * `sources/*` row (consistent chrome — fixes the "why do some files
 * show the pane and others don't" inconsistency the operator flagged
 * 2026-06-28) and surfaces a CTA that fires the first-assess directive
 * via `useSourceAssessAction(file, "first")`.
 *
 * The directive NEVER appears as a user-visible chat turn. The
 * concierge receives it as a system event and either (a) routes to
 * `m-ingest-verify` (Part H §14, NOT YET BUILT in code) or (b) falls
 * back to `lx_source_assess` for a single-source assess. Only the
 * agent's response renders to the user.
 */
function PaneMissing({ file }: { file: FileRef }) {
  const { t } = useTranslation();
  const { handleAction: handleVerify, status, canSend } = useSourceAssessAction(file, "first");

  return (
    <>
      <header className="mb-2 flex items-center gap-2">
        <AlertTriangle
          size={12}
          className="shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden
        />
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-cream-400">
          {t("assessment.pane.title")}
        </h3>
        <span
          data-testid="source-assessment-pane-missing-chip"
          className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
        >
          <AlertTriangle size={11} aria-hidden className="shrink-0" />
          {t("assessment.pane.missingChip")}
        </span>
      </header>
      <p className="mb-2 text-[12px] text-slate-600 dark:text-slate-300">
        {t("assessment.pane.missingExplain")}
      </p>
      <p className="mb-3 text-[11px] italic text-slate-500 dark:text-slate-400">
        {t("assessment.pane.missingHint")}
      </p>
      <div className="mt-auto flex flex-wrap items-center justify-end gap-2 border-t border-slate-200/70 pt-2 dark:border-coffee-700/40">
        {status === "sent" && (
          <span
            data-testid="source-assessment-pane-verify-sent"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300"
          >
            <Check size={11} aria-hidden />
            {t("assessment.pane.verifySent")}
          </span>
        )}
        {status === "error" && (
          <span
            data-testid="source-assessment-pane-verify-error"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 dark:text-red-300"
          >
            <AlertTriangle size={11} aria-hidden />
            {t("assessment.pane.verifyError")}
          </span>
        )}
        <button
          type="button"
          data-testid="source-assessment-pane-verify"
          onClick={() => handleVerify()}
          disabled={!canSend || status === "sending" || status === "sent"}
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-900/40 dark:disabled:hover:bg-amber-950/30"
        >
          {status === "sending" ? (
            <Loader2 size={11} aria-hidden className="animate-spin" />
          ) : (
            <Wrench size={11} aria-hidden />
          )}
          {t("assessment.pane.verifyCta")}
        </button>
      </div>
    </>
  );
}

// `file` is no longer a prop: it existed only to feed the removed re-evaluate
// CTA (which needed the filename for its directive). The body renders from the
// assessment alone.
function PaneBody({
  assessment,
  file,
  expanded,
  onToggleExpand,
}: {
  assessment: SourceAssessment;
  /** The open source — the DEEP REPORT resolves its sidecar from this path. */
  file: FileRef;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const { t } = useTranslation();
  const status = assessment.status;
  const isPending = status === "pending";
  const isDeclined = status === "declined";
  // ⚖ OPERATOR RULING 2026-08-09 — the per-document "Avaliar agora" /
  // "Reavaliar" CTA is REMOVED, along with its in-flight latch and the
  // reassess dialog trigger.
  //
  // The button was built for a world where ingestion was a mere upload and
  // nothing had read the document, so someone had to ask for an analysis. That
  // world is gone: **m-ingest-mission now runs an LLM evaluation at ingest**,
  // so by the time this pane can render, the brief already exists. The button
  // was offering to commission work that had already been done — and, because
  // it only enqueued a natural-language directive to the concierge, it was
  // never bound to a mission or harness at all.
  //
  // ⇒ **The ingest evaluation IS the assessment shown here.** It is a brief on
  // what the document is. If it needs to be deeper or different, the lever is
  // the eval PROMPT used by m-ingest-mission — one change that improves every
  // document — not a per-document button that re-asks an assistant to redo it.
  //
  // ⚠ Do not reintroduce a re-run control here without a new ruling. The
  // ambiguity it created was the operator's actual complaint: a pane showing
  // "not yet evaluated" next to a document that had been evaluated minutes
  // earlier.

  return (
    <>
      <header className="mb-2 flex items-center gap-2 font-sans">
        <FileText
          size={12}
          className="shrink-0 text-slate-400 dark:text-cream-500"
          aria-hidden
        />
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-cream-400">
          {t("assessment.pane.title")}
        </h3>
        {/* Right-aligned cluster. The re-evaluate CTA that used to lead it is
            GONE (ruling above); the badge + expand toggle remain.
            ⚠ The badge stays suppressed while `pending`, but the REASON has
            changed: it is no longer "the CTA already says so" — there is no
            CTA. It is that `pending` describes the LAWYER's annotation, and
            labelling a document "aguarda avaliação" when ingest has already
            evaluated it is the very confusion this ruling removed. */}
        <span className="ml-auto inline-flex items-center gap-1.5">
          {!isPending && (
            <AssessmentBadge
              status={status}
              role={assessment.role}
              weight={assessment.weight}
            />
          )}
          {/* Expand/collapse (operator 2026-07-30) — the pane can take the
              ENTIRE viewer pane for comfortable reading; the same button
              collapses it back to the bottom strip. */}
          <button
            type="button"
            data-testid="source-assessment-pane-expand"
            onClick={onToggleExpand}
            title={expanded ? t("assessment.pane.collapse") : t("assessment.pane.expand")}
            aria-label={expanded ? t("assessment.pane.collapse") : t("assessment.pane.expand")}
            aria-expanded={expanded}
            // Prominent (operator 2026-07-30 v2): the mode toggle is a primary
            // control on this pane — sized up + full-contrast, not chrome-shy.
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-400 bg-white/60 text-slate-600 transition-colors hover:border-slate-500 hover:bg-white hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/40 dark:border-coffee-500 dark:bg-coffee-800/60 dark:text-cream-200 dark:hover:border-coffee-500 dark:hover:bg-coffee-700 dark:hover:text-cream-50"
          >
            {expanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
        </span>
      </header>

      {/* Pending renders NO body line — the "Avaliar agora" CTA in the
          header replaced the old awaiting-message (operator 2026-07-30);
          its text lives on as the CTA's tooltip. */}
      {isDeclined && (
        <p className="mb-2 text-[11px] italic text-slate-500 dark:text-slate-400">
          {t("assessment.pane.declined")}
        </p>
      )}

      {/* ── The MACHINE analysis — renders INDEPENDENTLY of the lawyer's status ──
          🔴 Operator-reported 2026-08-09: "the file has been processed by the
          ingestion mission and categorized, but the actual analysis is not
          displaying." It was right, and there were TWO reasons stacked:
          the `.info.md` parser dropped every evaluation field (fixed in
          `useSourceInfoFile.ts`), and the fields block below is gated on
          `!isPending` — so even once parsed, a model analysis would stay hidden
          behind the LAWYER's pending state.

          ⚠ They are different authors with different lifecycles. A freshly
          ingested document normally has a COMPLETE machine evaluation and a
          PENDING lawyer assessment; that is the standard state, not a
          half-written record. Gating the model's work on the lawyer's turn
          hides analysis the product already paid for and told the lawyer about.

          ⚠ Labelled as machine-written, always. It must never read as the
          lawyer's own assessment — that would put words in their mouth on
          their own case file. */}
      {/* ⚠ Gated on `summary` ALONE since the CLASSIFICAÇÃO block was dropped
          (2026-08-11). It used to read `summary || evaluationRationale`, which
          after the removal would render a heading and a model attribution
          wrapped around nothing for a source carrying only a rationale. */}
      {assessment.summary && (
        <section
          data-testid="source-assessment-pane-machine"
          className={cn(
            "mb-2 rounded-md border border-slate-200/80 bg-white/60 px-2.5 py-2 dark:border-coffee-700/50 dark:bg-coffee-800/40",
            expanded ? "text-[14px]" : "text-[12px]",
          )}
        >
          <h4 className="mb-1 font-sans text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-cream-400">
            {t("assessment.machine.title")}
          </h4>
          {assessment.summary && (
            <p className="whitespace-pre-wrap text-slate-700 dark:text-cream-200">
              {assessment.summary}
            </p>
          )}
          {/* ⚖ The CLASSIFICAÇÃO block (`evaluationRationale`) is REMOVED —
              operator ruling 2026-08-11: "too much information with redundant
              potential is what lawyers hate". Display only: the rationale is
              still RECORDED in `.info` for audit and for the concierge, and
              `assessment.evaluationRationale` is still parsed. Do not
              reintroduce it here, and note there is deliberately no override
              note either — the ruling was unconditional. */}
          {assessment.evaluatedByModel && (
            // Provenance, not decoration: the lawyer weighs a machine reading
            // differently when they know which model produced it.
            <p className="mt-1.5 font-sans text-[10px] text-slate-400 dark:text-slate-500">
              {t("assessment.machine.byModel", { model: assessment.evaluatedByModel })}
            </p>
          )}
        </section>
      )}

      {/* ⚖ The conversation DEEP REPORT sits directly under ANÁLISE AUTOMÁTICA
          (operator ruling 2026-08-11): the machine's short read, then the
          machine's deep read, in one place. Renders nothing at all unless this
          source is a compound head WITH a report — see its docblock. */}
      <DeepReportSection file={file} expanded={expanded} />

      {!isPending && !isDeclined && (
        <dl
          data-testid="source-assessment-pane-fields"
          className={cn(
            "grid grid-cols-[max-content_1fr] gap-x-3",
            // Expanded = reading mode: the values inherit the aside's serif +
            // 15px; collapsed keeps the compact strip sizing.
            expanded ? "gap-y-2" : "gap-y-1 text-[12px]",
          )}
        >
          {assessment.role && (
            <Row label={t("assessment.fields.role")} value={assessment.role} />
          )}
          {assessment.weight && (
            <Row
              label={t("assessment.fields.weight")}
              value={t(`assessment.weight.${assessment.weight}`)}
            />
          )}
          {assessment.relevance && (
            <Row label={t("assessment.fields.relevance")} value={assessment.relevance} />
          )}
          {assessment.actors && (
            <Row label={t("assessment.fields.actors")} value={assessment.actors} />
          )}
          {assessment.notes && (
            <Row label={t("assessment.fields.notes")} value={assessment.notes} multiline />
          )}
        </dl>
      )}

      <footer
        data-testid="source-assessment-pane-footer"
        className="mt-2 flex flex-wrap items-center gap-3 border-t border-slate-200/70 pt-2 font-sans text-[10px] text-slate-500 dark:border-coffee-700/40 dark:text-slate-400"
      >
        {assessment.assessedBy && (
          <span className="inline-flex items-center gap-1">
            <User size={10} aria-hidden />
            {assessment.assessedBy}
          </span>
        )}
        {assessment.assessedAt && (
          <span className="inline-flex items-center gap-1">
            <Calendar size={10} aria-hidden />
            {formatStamp(assessment.assessedAt)}
          </span>
        )}
        {assessment.suggestedRole && !isPending && (
          <span className="inline-flex items-center gap-1">
            <Workflow size={10} aria-hidden />
            {t("assessment.pane.machineGuess", { role: assessment.suggestedRole })}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-slate-400 dark:text-slate-500">
          <Edit3 size={10} aria-hidden />
          {t("assessment.pane.editHint")}
        </span>
      </footer>

    </>
  );
}

function Row({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <>
      {/* Labels are CHROME — they stay sans even when the expanded pane
          switches the values to the reading serif. */}
      <dt className="font-sans text-slate-500 dark:text-cream-500">{label}</dt>
      <dd className={cn(multiline && "whitespace-pre-wrap")}>{value}</dd>
    </>
  );
}

function formatStamp(iso: string): string {
  // Best-effort ISO → "2026-06-27 14:32 UTC". Falls back to the raw
  // string if the input isn't parseable so a malformed stamp doesn't
  // crash the pane.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toISOString().slice(0, 10);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${date} ${hh}:${mm} UTC`;
}
